/**
 * 12. Implementation Wizard — the implementation phases, each with its live
 * state from GET /v1/implementation/phases and the reasons it is not done.
 *
 * Read-only on purpose. This page used to show a form (a legal-entity name, a
 * country, a currency, a fiscal-year start, a tax code) and a "Save & Continue"
 * button that posted `{}` to /implementation/phases/:key/complete. That
 * endpoint only re-checks the data — it saves nothing — so whatever was typed
 * was thrown away, and the last step's "Go Live" button did no more. Those
 * controls are gone until each step has a real write API behind it (roadmap
 * C1/C2, F10); until then the wizard reports what the server knows and does
 * not pretend to change it.
 *
 * Arabic and English (C7): a phase is named by its key and each reason by its
 * notice code (workspace-setup.controller.ts), in the interface's language;
 * the API's English label and message are shown only for a key or code the
 * catalog does not know. A failed load is shown through QueryError, in the
 * interface's language, with Try again.
 */
import { useId, useState } from "react";
import { Card, PageHeader, Badge, Button } from "@/components/ui";
import { QueryError } from "@/components/QueryError";
import { useApiQuery } from "@/lib/api/hooks";
import { useTranslation, type TFunction } from "@/lib/i18n";

interface Notice {
  code: string;
  message: string;
  /** SETUP_WAITING_ON and SETUP_PHASE_COMPLETE: `phase` is a phase key. */
  params?: { phase?: string };
}

interface Phase {
  key: string;
  label: string;
  complete: boolean;
  blocked: boolean;
  messages: string[];
  /** The same reasons as `messages`, by code. */
  notices?: Notice[];
}

/** A phase's name in the interface's language; the API's label otherwise. */
function phaseName(t: TFunction, key: string, label?: string): string {
  return t(`admin.wizard.phaseNames.${key}`, { defaultValue: label ?? key });
}

/** A phase's reasons, by code where the API sent them. */
function reasons(t: TFunction, phase: Phase): string[] {
  if (!phase.notices || phase.notices.length === 0) return phase.messages;
  return phase.notices.map((n) =>
    t(`admin.wizard.notices.${n.code}`, {
      defaultValue: n.message,
      phase: n.params?.phase ? phaseName(t, n.params.phase) : undefined,
    }),
  );
}

export function ImplementationWizardPage() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const baseId = useId();
  const tabId = (i: number) => `${baseId}phase-${i}`;
  const panelId = `${baseId}panel`;
  const { data, isPending, isError, error, refetch, isFetching } = useApiQuery<{
    phases: Phase[];
  }>(["implementation", "phases"], "/implementation/phases");

  const phases = data?.phases ?? [];
  const index = Math.min(current, Math.max(phases.length - 1, 0));
  const phase = phases[index];

  return (
    <div>
      <PageHeader
        title={t("admin.wizard.title")}
        subtitle={t("admin.wizard.subtitle")}
      />
      {isError ? (
        <QueryError
          error={error}
          retrying={isFetching}
          onRetry={() => void refetch()}
        />
      ) : isPending ? (
        <div className="atlas-skeleton" style={{ height: 120 }} />
      ) : !phase ? (
        // Loaded, but nothing to show. This used to fall through to the
        // skeleton above and look like a request that never finished (D8).
        <Card>
          <p role="status" style={{ marginBlock: "0 16px" }}>
            {t("admin.wizard.empty")}
          </p>
          <Button disabled={isFetching} onClick={() => void refetch()}>
            {t("session.checkAgain")}
          </Button>
        </Card>
      ) : (
        <div className="atlas-grid atlas-grid--3">
          <Card title={t("admin.wizard.phases")} className="mb-4">
            {/* The phases are a vertical tab list: choosing one shows its
                panel. The chosen phase is the selected tab, so choosing it
                again is not offered as an action (it was a button that did
                nothing, D57); arrow keys, Home and End move between them. */}
            <div
              role="tablist"
              aria-orientation="vertical"
              aria-label={t("admin.wizard.phases")}
              onKeyDown={(event) => {
                const last = phases.length - 1;
                const next =
                  event.key === "ArrowDown"
                    ? Math.min(index + 1, last)
                    : event.key === "ArrowUp"
                      ? Math.max(index - 1, 0)
                      : event.key === "Home"
                        ? 0
                        : event.key === "End"
                          ? last
                          : null;
                if (next === null) return;
                event.preventDefault();
                setCurrent(next);
                document.getElementById(tabId(next))?.focus();
              }}
            >
              {phases.map((p, i) => (
                <button
                  key={p.key}
                  type="button"
                  role="tab"
                  id={tabId(i)}
                  aria-selected={i === index}
                  aria-controls={panelId}
                  tabIndex={i === index ? 0 : -1}
                  onClick={() => setCurrent(i)}
                  className="atlas-btn atlas-btn--ghost"
                  style={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                    marginBlockEnd: 4,
                  }}
                >
                  <span>
                    {t("admin.wizard.step", {
                      number: i + 1,
                      label: phaseName(t, p.key, p.label),
                    })}
                  </span>
                  {/* A phase whose prerequisites are unmet is not done, whatever
                    its own probe says: never show a tick beside "blocked". */}
                  {p.blocked ? (
                    <Badge tone="danger">{t("admin.wizard.blocked")}</Badge>
                  ) : (
                    p.complete && <Badge tone="success">✓</Badge>
                  )}
                </button>
              ))}
            </div>
          </Card>
          <div role="tabpanel" id={panelId} aria-labelledby={tabId(index)}>
            <Card
              title={t("admin.wizard.phaseTitle", {
                number: index + 1,
                label: phaseName(t, phase.key, phase.label),
              })}
              className="mb-4"
            >
              {phase.blocked ? (
                <div className="atlas-blocked">
                  <h3 className="atlas-blocked__title">
                    {t("admin.wizard.whyBlocked")}
                  </h3>
                  <ul>
                    {reasons(t, phase).map((m) => (
                      <li key={m}>{m}</li>
                    ))}
                  </ul>
                </div>
              ) : phase.complete ? (
                <Badge tone="success">✓</Badge>
              ) : (
                <ul>
                  {reasons(t, phase).map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                </ul>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBlockStart: 16,
                }}
              >
                <Button
                  disabled={index === 0}
                  onClick={() => setCurrent(index - 1)}
                >
                  {t("shared.previous")}
                </Button>
                <Button
                  disabled={index >= phases.length - 1}
                  onClick={() => setCurrent(index + 1)}
                >
                  {t("shared.next")}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
